"use client";

import React, { useState } from "react";
import { useRouter, Link } from "@/i18n/routing";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { Button } from "./ui/button";
import { LogOut, Loader2, LayoutDashboard, Settings } from "lucide-react";
import { useUserStore } from "@/store/store";
import { logout, isAuthenticated } from "@/service/auth-service";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useLocale } from "next-intl";

export default function UserMenu() {
  const { user, clearAuth } = useUserStore();
  const router = useRouter();
  const t = useTranslations("general");
  const locale = useLocale();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<boolean | undefined>(undefined);

  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent multiple clicks
    
    setIsLoggingOut(true);
    
    try {
      await logout();
      clearAuth();
      router.push("/");
      router.refresh();
    } catch (error) {
      console.error("Logout error:", error);
      // Clear auth anyway even if logout API fails
      clearAuth();
      router.push("/");
      router.refresh();
    } finally {
      setIsLoggingOut(false);
      setDropdownOpen(false);
    }
  };

  if (!isAuthenticated() || !user) {
    return null;
  }

  const profileImage = user.profile_image || "/avatar.png";
  const displayName = user.name || user.email || "User";
  
  // Determine dashboard route based on user role
  const dashboardRoute = user.role_id === 3 
    ? "/app/trainer/dashboard" 
    : "/app/client/dashboard";
  
  const settingsRoute = user.role_id === 3 
    ? "/app/trainer/settings" 
    : "/app/client/settings";

  return (
    <DropdownMenu 
      open={isLoggingOut ? true : dropdownOpen}
      onOpenChange={(open) => {
        // Prevent closing during logout
        if (isLoggingOut && !open) {
          return; // Don't allow closing while logging out
        }
        if (!isLoggingOut) {
          setDropdownOpen(open);
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "rounded-full p-0 h-auto bg-transparent hover:bg-transparent focus:bg-transparent",
            "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          )}
        >
          <div className="flex items-center gap-3 px-3 py-2 rounded-full hover:bg-black/30 transition-colors">
            <div className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-primary/30">
              <Image
                src={profileImage}
                alt={displayName}
                width={40}
                height={40}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to default avatar if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.src = "/avatar.png";
                }}
              />
            </div>
            <span className="hidden md:block text-white font-poppins text-sm max-w-[120px] truncate">
              {displayName}
            </span>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={locale === "ar" ? "start" : "end"}
        className="w-56 bg-black-bg border-[#636363] text-white"
      >
        <div className="px-2 py-3 border-b border-[#636363]">
          <p className="text-sm font-medium text-white truncate">{displayName}</p>
          <p className="text-xs text-gray-400 truncate">{user.email}</p>
        </div>
        <DropdownMenuItem asChild>
          <Link
            href={dashboardRoute}
            className={cn(
              "cursor-pointer text-white focus:bg-primary/20 focus:text-white flex items-center"
            )}
          >
            <LayoutDashboard className={cn("h-4 w-4", locale === "ar" ? "ml-2" : "mr-2")} />
            <span>{t("dashboard")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            href={settingsRoute}
            className={cn(
              "cursor-pointer text-white focus:bg-primary/20 focus:text-white flex items-center"
            )}
          >
            <Settings className={cn("h-4 w-4", locale === "ar" ? "ml-2" : "mr-2")} />
            <span>{t("settings")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#636363]" />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            if (!isLoggingOut) {
              handleLogout();
            }
          }}
          disabled={isLoggingOut}
          className={cn(
            "cursor-pointer text-white focus:bg-primary/20 focus:text-white",
            isLoggingOut && "opacity-50 cursor-not-allowed"
          )}
        >
          {isLoggingOut ? (
            <>
              <Loader2 className={cn("h-4 w-4 animate-spin", locale === "ar" ? "ml-2" : "mr-2")} />
              <span>{t("logging_out") || "Logging out..."}</span>
            </>
          ) : (
            <>
              <LogOut className={cn("h-4 w-4", locale === "ar" ? "ml-2" : "mr-2")} />
              <span>{t("logout")}</span>
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

